import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

const STORAGE_KEY = "web-support:server-error";

/** 可跨刷新保留的错误快照；数据字段为格式化后的原始值。 */
export interface ServerErrorDetails {
  status: number | null;
  method: string;
  url: string;
  occurredAt: string;
  params: string;
  headers: string;
  requestData: string;
  responseData: string;
}

/** 格式化 JSON 和文本，保留表单字段、文件信息及循环引用标记。 */
export function formatErrorValue(value: unknown): string {
  if (value === undefined) return "undefined";
  if (typeof value === "string") return value;
  const ancestors: object[] = [];
  return JSON.stringify(value, function (_key, item: unknown) {
    if (typeof item === "bigint") return item.toString();
    if (item instanceof FormData) return Array.from(item.entries());
    if (item instanceof URLSearchParams) return item.toString();
    if (item instanceof Blob) {
      return { ...(item instanceof File ? { name: item.name } : {}), type: item.type, size: item.size };
    }
    if (item && typeof item === "object") {
      while (ancestors.length && ancestors[ancestors.length - 1] !== this) ancestors.pop();
      if (ancestors.includes(item)) return "[Circular reference]";
      ancestors.push(item);
    }
    return item;
  }, 2);
}

/** 提取所需字段，避免把 Axios 请求实例和函数带入路由或存储。 */
export function createServerErrorDetails(response?: AxiosResponse, config?: AxiosRequestConfig): ServerErrorDetails {
  const requestConfig = response?.config ?? config;
  return {
    status: response?.status ?? null,
    method: requestConfig?.method?.toUpperCase() ?? "",
    url: requestConfig ? axios.getUri(requestConfig) : "",
    occurredAt: new Date().toISOString(),
    params: formatErrorValue(requestConfig?.params),
    headers: formatErrorValue(requestConfig?.headers),
    requestData: formatErrorValue(requestConfig?.data),
    responseData: formatErrorValue(response?.data),
  };
}

/** 跳转前保存快照；存储受限时仍可通过路由状态展示本次错误。 */
export function saveServerErrorDetails(details: ServerErrorDetails): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(details));
  } catch (error) {
    console.warn("保存错误诊断信息失败", error);
  }
}

/** 刷新或直接打开错误页时，读取当前标签页最近一次错误。 */
export function readServerErrorDetails(): ServerErrorDetails | null {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}
